import React, { useState } from "react";
import { toast } from "react-toastify";
import Table from "../../../../components/elements/table/Table";
import { setTitle } from "../../../../helpers/MetaTag";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import Badge from "../../../../components/elements/Badge";
import Search from "../../../../components/form/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { enquiryDelete, enquiryList } from "../../../../services/enquiryService";
import { classNames } from "../../../../helpers/classNames";
import ActionBar from "../../../../components/elements/ActionBar";

const Enquiry = () => {
  setTitle("Enquiry Management");
  const { t } = useTranslation();
  const { session, loginData } = useSelector((state) => state?.authProvider);
  const queryClient = useQueryClient();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: ["enquiry", filters, search],
    queryFn: async () => {
      const { ...restFilters } = filters || {};

      const queryParams = {
        ...restFilters,
        ...(search && { keyword: search }),
      };

      const res = await enquiryList(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs || {};
    },
  });

  const options = [
    {
      id: "enquiryDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "Enquiry Delete",
    },
  ];

  const tableHeadData = [
    {
      _id: 1,
      name: "Name",
      align: "left",
      isSort: true,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-name" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "name" }));
        }
      },
    },
    {
      _id: 2,
      name: "Email",
      align: "left",
    },
    {
      _id: "message",
      name: "Message",
      align: "left",
    },
    {
      _id: 4,
      name: "Organization",
      align: "left",
    },
    {
      _id: "responded",
      name: "Responded",
      align: "left",
      isFilter: false,
      isFilterSearch: false,
      // onFilter: (val) => {
      //   if (val?.length > 0 && val?.length === 1) {
      //     setFilters((pre) => ({
      //       ...pre,
      //       responded: val?.map((item) => item?.value),
      //       page: 1,
      //     }));
      //   } else {
      //     setFilters((pre) => {
      //       delete pre?.status;
      //       return { ...pre };
      //     });
      //   }
      // },
      // filterData: [
      //   { name: "Responded", value: true, type: "checkbox" },
      //   { name: "Non Responded", value: false, type: "checkbox" },
      // ],
    },
    {
      _id: 7,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: classNames(item?.name) || "-",
          // image: item?.image?.url,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.email || "-",
        },
        {
          _id: "message",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.message || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.organizationDetail?.name || "-",
        },
        {
          _id: "responded",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Badge type={!item?.responded ? "info" : "danger"} label={!item?.responded ? "Non Responded" : "Responded"} />
            </div>
          ),
        },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 3,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await enquiryDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        setSelectedItems([]);
        queryClient.setQueryData(["enquiry", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        queryClient.invalidateQueries(["enquiry", filters, search]);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function actionHandler(val) {
    switch (val) {
      case "enquiryDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-normal text-lg xl:text-3xl text-primary">{"Enquiry Management"}</div>
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect value={filters?.limit} allValue={data?.metadata?.totalDocs} onChange={(val) => handleFilterChange("limit", val, setFilters)} />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-md bg-secondary text-surface-01 text-xs px-1 py-1 gap-1">
                <span className="inline-block px-1">{selectedItems?.length} Selected</span>
                <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(val) => {
            console.log(val);
          }}
          isFetching={isFetching}
          isOrderingDisabled={true}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        // selectable
        />
        <DeleteModal
          open={deleteModal?.isOpen}
          loading={deletePending}
          onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
          deleteHandler={() => {
            deleteHandler(deleteModal?.data?._id);
          }}
        />
      </div>
    </div>
  );
};

export default Enquiry;
